/**
* @license Apache-2.0
*
* Copyright (c) 2025 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

/* eslint-disable no-invalid-this */

'use strict';

// MODULES //

import IS_LITTLE_ENDIAN from '@stdlib/assert-is-little-endian';
import { isPrimitive as isNumber } from '@stdlib/assert-is-number';
import { isPrimitive as isBoolean } from '@stdlib/assert-is-boolean';
import { isPrimitive as isBigInt } from '@stdlib/assert-is-bigint';
import isComplexLike from '@stdlib/assert-is-complex-like';
import isAllowedCast from '@stdlib/ndarray-base-assert-is-allowed-data-type-cast';
import complexDType from '@stdlib/complex-dtype';
import Boolean from '@stdlib/boolean-ctor';
import format from '@stdlib/error-tools-fmtprodmsg';
import PRIVATE_BUFFER from './private_buffer.js';
import boolean2number from './boolean2number.js';
import number2boolean from './number2boolean.js';
import complex2boolean from './complex2boolean.js';
import bigint2boolean from './bigint2boolean.js';
import defaults from './defaults.json';


// MAIN //

/**
* Returns a function for writing field data.
*
* @private
* @param {Object} obj - field object
* @param {string} method - data view method name
* @returns {Function} function for writing field data
*/
function setBoolean( obj, method ) {
	return setter;

	/**
	* Writes a boolean value to an underlying byte buffer.
	*
	* @private
	* @param {*} value - value to set
	* @throws {TypeError} cannot cast provided values to field data type
	*/
	function setter( value ) {
		var view;
		var dt;
		var v;
		if ( isBoolean( value ) ) {
			dt = 'bool';
			v = boolean2number( value );
		} else if ( isNumber( value ) ) {
			dt = defaults.dtypes.real;
			v = boolean2number( number2boolean( value ) );
		} else if ( isBigInt( value ) ) {
			dt = 'int64'; // note: the specific integer data type (int64 vs uint64) should not matter here
			v = boolean2number( bigint2boolean( value ) );
		} else if ( isComplexLike( value ) ) {
			dt = complexDType( value ) || defaults.dtypes.complex;
			v = boolean2number( complex2boolean( value ) );
		} else {
			dt = 'generic';
			v = Boolean( value );
		}
		if ( !isAllowedCast( dt, obj.type, obj.castingMode ) ) {
			throw new TypeError( format( 'invalid assignment. Assigned value cannot be cast to the data type of `%s`. Data types: [%s, %s].', obj.name, obj.type, dt ) );
		}
		view = this[ PRIVATE_BUFFER ];
		view[ method ]( obj.byteOffset, v, IS_LITTLE_ENDIAN );
	}
}


// EXPORTS //

export default setBoolean;
